import { useState, useMemo } from "react";
import { X, Eye, Send, AlertCircle, CheckCircle2, MapPin } from "lucide-react";
import {
  EmailTemplate,
  EmailMethod,
  EmailResult,
} from "../api/useAdminEmails";
import { TableSelector, TableInfo } from "./TableSelector";

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (
    template: EmailTemplate,
    method: EmailMethod,
    tableIds: string[],
    dryRun: boolean,
    customBroadcastId?: string
  ) => void;
  isLoading: boolean;
  tables: TableInfo[];
  bulkResults?: Array<{
    tableId: string;
    tableName: string;
    success: boolean;
    emails: EmailResult[];
  }>;
}

const EMAIL_TEMPLATE_OPTIONS = [
  {
    value: EmailTemplate.BOOKING_REMINDER,
    label: "T-48: Booking Reminder",
    description: "Remind users about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_REMINDER_51H,
    label: "T-51: Booking Reminder",
    description: "Remind users about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_REMINDER_72H,
    label: "T-72: Booking Reminder",
    description: "Remind users about their upcoming booking",
  },
  {
    value: EmailTemplate.BOOKING_DETAILS,
    label: "T-24: Booking Details",
    description: "Send complete booking information and table details",
  },
  {
    value: EmailTemplate.BOOKING_ABOUT_TO_HAPPEN,
    label: "T-1: Booking About to Happen",
    description: "Notify users that their booking is about to happen and they should join the chat.",
  },
  {
    value: EmailTemplate.POST_BOOKING_SURVEY,
    label: "T+16: Post-Booking Survey",
    description: "Request feedback after the event",
  },
  {
    value: EmailTemplate.BOOKING_UPDATED,
    label: "Booking Updated",
    description: "Notify users their booking details have changed",
  },
  {
    value: EmailTemplate.UNABLE_TO_BE_SEATED,
    label: "Unable To Be Seated",
    description: "Notify users we were unable to seat them at a table",
  },
];

export function EmailModal({
  isOpen,
  onClose,
  onSend,
  isLoading,
  tables,
  bulkResults,
}: EmailModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate>(
    EmailTemplate.BOOKING_DETAILS
  );
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [neighborhoodFilter, setNeighborhoodFilter] = useState<string>("all");
  const [customBroadcastId, setCustomBroadcastId] = useState<string>("");

  // Get unique neighborhoods from tables
  const availableNeighborhoods = useMemo(() => {
    const neighborhoods = new Set<string>();
    tables.forEach((table) => {
      if (table.neighborhood) {
        neighborhoods.add(table.neighborhood);
      }
    });
    return Array.from(neighborhoods).sort();
  }, [tables]);

  // Filter tables by neighborhood
  const filteredTables = useMemo(() => {
    if (neighborhoodFilter === "all") {
      return tables;
    }
    return tables.filter(
      (table) =>
        table.neighborhood === neighborhoodFilter ||
        table.id === "unassigned-users"
    );
  }, [tables, neighborhoodFilter]);

  if (!isOpen) return null;

  const handleClose = () => {
    setSelectedTableIds([]);
    setNeighborhoodFilter("all");
    setCustomBroadcastId("");
    onClose();
  };

  const trimmedBroadcastId = customBroadcastId.trim();
  const broadcastIdOverride = trimmedBroadcastId.length > 0 ? trimmedBroadcastId : undefined;

  const handleDryRun = () => {
    if (selectedTableIds.length === 0) return;
    onSend(selectedTemplate, EmailMethod.BROADCAST, selectedTableIds, true, broadcastIdOverride);
  };

  const handleSend = () => {
    if (selectedTableIds.length === 0) return;
    onSend(selectedTemplate, EmailMethod.BROADCAST, selectedTableIds, false, broadcastIdOverride);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <div>
            <h2 className="text-lg font-bold">Send Table Emails</h2>
            <p className="text-base-content/60 text-xs">
              Select tables and choose an email template
            </p>
          </div>
          <button
            onClick={handleClose}
            className="btn btn-ghost btn-xs btn-circle w-8 h-8 p-1"
            disabled={isLoading}
          >
            <X width={32} height={32} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Email Template Selection */}
          <div>
            <label className="label py-1">
              <span className="label-text text-xs font-semibold">
                Email Template
              </span>
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) =>
                setSelectedTemplate(e.target.value as EmailTemplate)
              }
              className="select select-bordered select-sm w-full"
            >
              {EMAIL_TEMPLATE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} - {option.description}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Broadcast ID Override */}
          <div>
            <label className="label py-1">
              <span className="label-text text-xs font-semibold">
                Custom Broadcast ID
              </span>
              <span className="label-text-alt text-[10px] text-base-content/60">
                Optional — overrides the template's default
              </span>
            </label>
            <input
              type="text"
              value={customBroadcastId}
              onChange={(e) => setCustomBroadcastId(e.target.value)}
              placeholder="e.g. 65"
              className="input input-bordered input-sm w-full"
              inputMode="numeric"
            />
          </div>

          {/* Neighborhood Filter */}
          {availableNeighborhoods.length > 0 && (
            <div>
              <label className="label py-1">
                <span className="label-text text-xs font-semibold flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Filter by Neighborhood
                </span>
              </label>
              <select
                value={neighborhoodFilter}
                onChange={(e) => {
                  setNeighborhoodFilter(e.target.value);
                  setSelectedTableIds([]);
                }}
                className="select select-bordered select-sm w-full"
              >
                <option value="all">All Neighborhoods</option>
                {availableNeighborhoods.map((neighborhood) => (
                  <option key={neighborhood} value={neighborhood}>
                    {neighborhood}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Table Selection */}
          <div>
            <TableSelector
              tables={filteredTables}
              selectedTableIds={selectedTableIds}
              onSelectionChange={setSelectedTableIds}
            />
          </div>

          {/* Dry Run Results - Expandable Panel */}
          {bulkResults && bulkResults.length > 0 && (
            <details
              className="collapse collapse-arrow bg-base-200 border border-base-300"
              open
            >
              <summary className="collapse-title min-h-0 py-2 px-3 cursor-pointer font-semibold text-sm flex items-center gap-2">
                <span>Dry Run Results</span>
                {bulkResults[0].success ? (
                  <CheckCircle2 className="w-4 h-4 text-success" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-error" />
                )}
              </summary>
              <div className="collapse-content px-3 pb-3 space-y-3">
                {/* Summary */}
                <div className="text-xs">
                  <p className="font-semibold">{bulkResults[0].tableName}</p>
                  <p className="text-base-content/60">
                    {bulkResults[0].emails.length} recipient(s)
                  </p>
                </div>

                {/* Recipients List */}
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {bulkResults[0].emails.map((email, emailIndex) => (
                    <div
                      key={emailIndex}
                      className="flex items-center justify-between text-[11px] p-1 bg-base-100 rounded"
                    >
                      <span className="truncate">{email.email}</span>
                      {email.success ? (
                        <CheckCircle2 className="w-3 h-3 text-success shrink-0 ml-2" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-error shrink-0 ml-2" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Single Request Preview */}
                {bulkResults[0].emails[0]?.request && (
                  <details className="collapse collapse-arrow bg-base-100 border border-base-300">
                    <summary className="collapse-title min-h-0 py-1.5 px-2 cursor-pointer font-medium text-[11px]">
                      View Customer.io API Request
                    </summary>
                    <div className="collapse-content px-2 pb-2">
                      <pre className="bg-base-300 p-2 rounded overflow-auto max-h-48 text-[9px] leading-tight">
                        {JSON.stringify(
                          bulkResults[0].emails[0].request,
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </details>
                )}
              </div>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-base-300">
          <div className="flex gap-2">
            <button
              onClick={handleDryRun}
              className="btn btn-secondary btn-sm gap-1"
              disabled={isLoading || selectedTableIds.length === 0}
              title={
                selectedTableIds.length === 0 ? "Select at least one table" : ""
              }
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Eye className="w-3 h-3" />
              )}
              Dry Run
            </button>
            <button
              onClick={handleSend}
              className="btn btn-primary btn-sm gap-1"
              disabled={isLoading || selectedTableIds.length === 0}
              title={
                selectedTableIds.length === 0 ? "Select at least one table" : ""
              }
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Send className="w-3 h-3" />
              )}
              Send Emails
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
